import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, effect, inject } from '@angular/core';
import { ToastService, ToastType } from '@shared/services/toast.service';

interface ToastVariantStyles {
  bgClass: string;
  ariaLive: 'polite' | 'assertive';
  ariaRole: 'status' | 'alert';
}

// Opaque tints = Figma 40% alpha pre-composited over white. Keeps the Figma
// palette intent while staying legible over any page background.
const TOAST_VARIANTS: Record<ToastType, ToastVariantStyles> = {
  success: {
    bgClass: 'bg-brand-toast-success',
    ariaLive: 'polite',
    ariaRole: 'status',
  },
  error: {
    bgClass: 'bg-brand-toast-error',
    ariaLive: 'assertive',
    ariaRole: 'alert',
  },
  info: {
    bgClass: 'bg-brand-toast-info',
    ariaLive: 'polite',
    ariaRole: 'status',
  },
};

@Component({
  selector: 'rec-toast-container',
  standalone: true,
  imports: [NgClass],
  templateUrl: './toast-container.component.html',
  styles: [
    `
      :host {
        display: contents;
      }

      .rec-toast {
        animation: rec-toast-in 180ms ease-out both;
      }

      @keyframes rec-toast-in {
        from {
          opacity: 0;
          transform: translateY(-6px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .rec-toast {
          animation: none;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToastContainerComponent {
  private readonly toastService = inject(ToastService);
  private readonly host = inject(ElementRef<HTMLElement>);

  readonly toasts = this.toastService.toasts;

  constructor() {
    // Montamos el host como último hijo de <body>:
    //  - Escapa el stacking context de ancestros (tui-root, app-root).
    //  - Al re-appenderlo cada vez que cambian los toasts, quedamos después
    //    de cualquier overlay de terceros (cal.com, Veriff) que también se
    //    appendea al body. Los z-index grandes se clampean a int32-max en los
    //    navegadores, así que el orden DOM decide los empates.
    effect(() => {
      if (this.toasts().length > 0) {
        document.body.appendChild(this.host.nativeElement);
      }
    });
  }

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  variantFor(type: ToastType): ToastVariantStyles {
    return TOAST_VARIANTS[type];
  }
}
